import { addDays } from 'date-fns';
import { createSelector, Selector } from 'reselect';

import { PENDING } from '../../design-system/constants';
import { APICallStatus } from '../../design-system/types';
import { RootState } from '../../shared/redux/types';
import { getToday, getUTCDate } from '../../shared/utilities/date';

import { currencySubunit } from './constants';
import {
    Contract,
    ContractEditStep,
    SerializedLegalInformation,
    QuotePricing,
    DisplayQuotePricing,
    ProofDocument,
    PostProofOfDocuments,
    PaymentScheduleViewModel,
    ExistingPaymentSchedule,
    InstalmentViewModel,
    ExistingInstalment,
    Price,
} from './types';

export const getContract = (state: RootState): Contract | null => state.contract.entity;

function transformInstalmentToViewModel(instalment: ExistingInstalment): InstalmentViewModel {
    return {
        ...instalment,
        dueDate: getUTCDate(instalment.dueDate),
        paidAt: instalment.paidAt ? getUTCDate(instalment.paidAt) : null,
        price: {
            amount: parseInt(instalment.price.amount as string) / currencySubunit,
            currency: instalment.price.currency,
        },
    };
}

export function transformPaymentScheduleToViewModel(
    paymentSchedule: ExistingPaymentSchedule,
): PaymentScheduleViewModel {
    return {
        currency: paymentSchedule.currency,
        depositInstalments: paymentSchedule.instalments
            .filter((instalment) => instalment.type === 'deposit')
            .map(transformInstalmentToViewModel),
        balanceInstalments: paymentSchedule.instalments
            .filter((instalment) => instalment.type === 'balance')
            .map(transformInstalmentToViewModel),
        token: paymentSchedule.token,
    };
}

export const getContractPaymentSchedule = createSelector(getContract, (contract) => {
    return contract === null || contract.paymentSchedule === null
        ? {
              currency: undefined,
              depositInstalments: [],
              balanceInstalments: [],
              token: '',
          }
        : transformPaymentScheduleToViewModel(contract.paymentSchedule);
});

export const getContractViewModel = createSelector(
    getContract,
    getContractPaymentSchedule,
    (contract, paymentSchedule) => {
        if (contract === null) {
            return null;
        }
        return {
            ...contract,
            paymentSchedule,
        };
    },
);

export const getContractAPICallStatus = (state: RootState): APICallStatus | undefined =>
    state.status.contract.get;

export const getLegalInformation = (state: RootState): SerializedLegalInformation[] =>
    state.legalInformation.entity;

export const getCurrentStep = (state: RootState): ContractEditStep => state.contract.currentStep;

export const postContractAPICallStatus = (state: RootState): APICallStatus | undefined =>
    state.status.contract.post;

export const getContractTravelersInformation = createSelector(getContract, (contract) =>
    contract === null
        ? [{ firstName: '', lastName: '', country: '', birthDate: '' }]
        : contract.travelersInformation,
);

export const getLegalInformationFormattedForSelect = createSelector(
    getLegalInformation,
    (legalInformation) =>
        legalInformation.map((legal) => {
            return {
                value: legal.reference,
                label: `${legal.destination} - ${legal.language.toUpperCase()}`,
            };
        }),
);

export const getQuotePricing = (state: RootState): QuotePricing | null => state.quotePricing.entity;

export const getQuotePricingAPICallStatus = (state: RootState): APICallStatus | undefined =>
    state.status.quotePricing.get;

export const getInitialQuotePricing = (
    numberOfTravelers: number,
): Selector<RootState, DisplayQuotePricing> =>
    createSelector(getQuotePricing, (pricing): DisplayQuotePricing => {
        if (pricing) {
            const { domesticFlights, internationalFlights, landServices } = pricing.fees;
            const totalPrice =
                domesticFlights.localAgency.amount +
                internationalFlights.localAgency.amount +
                landServices.localAgency.amount;
            return {
                currency: pricing.localAgencyCurrency,
                totalPrice,
                pricePerPax: numberOfTravelers > 0 ? totalPrice / numberOfTravelers : 0,
                internationalFlightsPrice: internationalFlights.localAgency.amount,
                domesticFlightsPrice: domesticFlights.localAgency.amount,
                landServicesPrice: landServices.localAgency.amount,
            };
        }
        return {
            currency: undefined,
            totalPrice: 0,
            pricePerPax: 0,
            internationalFlightsPrice: 0,
            domesticFlightsPrice: 0,
            landServicesPrice: 0,
        };
    });

export const getInitialProofOfDocuments = (state: RootState): ProofDocument[] =>
    state.quotePricing.entity?.proofDocuments || [];

export const getUploadedNotAttachedProofOfDocuments = (state: RootState): ProofDocument[] =>
    state.quotePricing.uploadedNotAttachedProofOfDocuments;

const postProofDocumentsAPICallStatus = (state: RootState): PostProofOfDocuments =>
    state.status.quotePricing?.postProofDocuments || {};

export const areProofOfDocumentsLoading = createSelector(
    postProofDocumentsAPICallStatus,
    (documents) => {
        return Object.values(documents).some((value) => value === PENDING);
    },
);

export const getFailedProofOfDocuments = (state: RootState): File[] =>
    state.quotePricing.failedProofOfDocuments;

export const getAllValidProofOfDocuments = createSelector(
    [getInitialProofOfDocuments, getUploadedNotAttachedProofOfDocuments],
    (initialProofOfDocuments, uploadedNotAttachedProofOfDocuments) =>
        initialProofOfDocuments.concat(uploadedNotAttachedProofOfDocuments),
);

export const getChangeQuotePricesStatus = (state: RootState): APICallStatus | undefined =>
    state.status.quotePricing.post;

export const calculateDepositDefaultDueDate = (): Date => addDays(getToday(), 5);

export const shouldRecreatePaymentSchedule = (state: RootState): boolean =>
    state.contract.shouldRecreatePaymentSchedule;

export const hasPaidOrWaitingForApprovalInstalments = createSelector(
    getContractPaymentSchedule,
    (paymentSchedule) => {
        const instalments = paymentSchedule.depositInstalments.concat(
            paymentSchedule.balanceInstalments,
        );
        return instalments.some((instalment) => instalment.status !== 'created');
    },
);

export const getPreviousPaymentSchedule = (state: RootState): PaymentScheduleViewModel => {
    const { previousPaymentSchedule, nextCurrency } = state.contract;

    if (!previousPaymentSchedule || !nextCurrency) {
        throw new Error('undefined previous payment schedule or new currency');
    }

    const clearAmountWithNextCurrency = (instalment: InstalmentViewModel) => ({
        ...instalment,
        price: {
            amount: '',
            currency: nextCurrency,
        },
    });

    return {
        ...previousPaymentSchedule,
        currency: nextCurrency,
        depositInstalments: previousPaymentSchedule.depositInstalments.map(
            clearAmountWithNextCurrency,
        ),
        balanceInstalments: previousPaymentSchedule.balanceInstalments.map(
            clearAmountWithNextCurrency,
        ),
    };
};

export const shouldDisplayChangeCurrencyModal = (state: RootState): boolean => {
    if (
        state.contract.shouldRecreatePaymentSchedule ||
        !state.contract.entity ||
        state.contract.entity?.paymentSchedule === null
    ) {
        return false;
    }

    return true;
};

export const getContractDisplayPrice = (state: RootState): Price | null => {
    if (!state.contract.entity || state.contract.entity.price === null) {
        return null;
    }
    const {
        price: { currency, amount },
        currencies,
    } = state.contract.entity;
    const subunit = Math.pow(10, currencies[currency].subunit);
    const parsePrice = parseFloat(amount as string) / subunit;

    return {
        amount: parsePrice,
        currency: currency,
    };
};

export const getPostPaymentScheduleStatus = (state: RootState): APICallStatus | undefined =>
    state.status.paymentSchedule.post;

export const getPutPaymentScheduleStatus = (state: RootState): APICallStatus | undefined =>
    state.status.paymentSchedule.put;
